import {Options} from '@fl/cmsch-fe-library';

import {TFunction} from 'app/translations';

interface Props {
    farmIds: Array<number> | null;
    centerIds: Array<number> | null;
    hasAccessToAllOrganizationCenters: boolean;
    hasAccessToAllFarms: boolean;
    managementOrganizationsFarmTooltipOptions: Options<number>;
    managementOrganizationsCenterTooltipOptions: Options<number>;
    t: TFunction<'admin'>;
}

/**
 * Builds the hover tooltip text for farm and center lists in management tables.
 *
 * @param props Farm and center options with access flags.
 * @returns A multiline tooltip string shown in the table cell.
 *
 * @example
 * genFarmsAndCentersTooltip({
 *     farmIds: [],
 *     centerIds: [],
 *     hasAccessToAllOrganizationCenters: false,
 *     hasAccessToAllFarms: true,
 *     managementOrganizationsFarmTooltipOptions: [],
 *     managementOrganizationsCenterTooltipOptions: [],
 *     t,
 * });
 */
export const genFarmsAndCentersTooltip = ({
    farmIds,
    centerIds,
    hasAccessToAllOrganizationCenters,
    hasAccessToAllFarms,
    managementOrganizationsFarmTooltipOptions,
    managementOrganizationsCenterTooltipOptions,
    t,
}: Props): string => {
    const farms = managementOrganizationsFarmTooltipOptions
        .filter(f => farmIds?.includes(f.value))
        .map(f => f.label)
        .join(',\n');
    const centers = managementOrganizationsCenterTooltipOptions
        .filter(c => centerIds?.includes(c.value))
        .map(c => c.label)
        .join(',\n');

    const farmsTooltip = !hasAccessToAllFarms && farms.length === 0
        ? []
        : [t('farmsTooltip'), hasAccessToAllFarms ? t('allFarms') : farms];

    const centersTooltip = !hasAccessToAllOrganizationCenters && centers.length === 0
        ? []
        : [t('organizationsTooltip'), hasAccessToAllOrganizationCenters ? t('allOrganizations') : centers];

    return [...farmsTooltip, ...centersTooltip].join('\n');
};
